import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchCaseOkCases } from "../../store/common/casesSlice";
import { sendCaseOkEmails, resetStatus } from "../../store/admin/emailSlice";
import { useToast } from "../../components/utilities/Toast";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminMetaRow,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const PAGE_SIZE = 50;

const statusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "files pending") return "bg-red-50 text-red-700 ring-red-200";
  if (s === "submitted") return "bg-orange-50 text-orange-700 ring-orange-200";
  if (s === "query pending")
    return "bg-amber-50 text-amber-800 ring-amber-200";
  if (s === "under process")
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (s === "completed") return "bg-sky-50 text-sky-700 ring-sky-200";
  if (s.includes("milling"))
    return "bg-violet-50 text-violet-700 ring-violet-200";
  return "bg-gray-50 text-gray-600 ring-gray-200";
};

const CaseOk: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { caseOkCases, caseOkTotalPages, isLoading, error } = useAppSelector(
    (state) => state.adminCases
  );
  const emailState = useAppSelector((state) => state.emailSender);
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch cases
  useEffect(() => {
    dispatch(fetchCaseOkCases({ page: currentPage, limit: PAGE_SIZE }));
    setSelectedCaseIds(new Set());
  }, [dispatch, currentPage]);

  // Handle email send results
  useEffect(() => {
    if (emailState.success) {
      showToast(
        `Emails sent to: ${emailState.finalEmails.join(", ")}`,
        "success"
      );
      dispatch(fetchCaseOkCases({ page: currentPage, limit: PAGE_SIZE }));
      dispatch(resetStatus());
      setSelectedCaseIds(new Set());
    } else if (emailState.error) {
      const msg =
        emailState.missingCaseIds.length > 0
          ? `Missing emails for: ${emailState.missingCaseIds.join(", ")}`
          : emailState.error;
      showToast(msg, "error");
      dispatch(resetStatus());
    }
  }, [emailState, dispatch, currentPage, showToast]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedCaseIds((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedCaseIds((prev) =>
      prev.size === caseOkCases.length
        ? new Set()
        : new Set(caseOkCases.map((c) => c.case_id))
    );
  }, [caseOkCases]);

  const handleSendEmails = useCallback(() => {
    if (!selectedCaseIds.size) return showToast("No cases selected", "error");
    setShowConfirmModal(true);
  }, [selectedCaseIds, showToast]);

  const finalSendBy =
    user?.role === "Super Admin" ? user?.user_name : employee?.emp_name;

  // Confirm send
  const confirmSend = useCallback(() => {
    dispatch(
      sendCaseOkEmails({
        case_id: Array.from(selectedCaseIds),
        sendBy: finalSendBy || "",
      })
    );

    setShowConfirmModal(false);
  }, [dispatch, selectedCaseIds]);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= caseOkTotalPages) setCurrentPage(page);
    },
    [caseOkTotalPages]
  );

  const isAllSelected = useMemo(
    () => caseOkCases.length > 0 && selectedCaseIds.size === caseOkCases.length,
    [caseOkCases, selectedCaseIds]
  );

  const TableRow = React.memo(({ record }: { record: any }) => (
    <tr className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors">
      <td className={`${adminTableCell} text-center`}>
        <input
          type="checkbox"
          checked={selectedCaseIds.has(record.case_id)}
          onChange={() => toggleSelect(record.case_id)}
          className="h-4 w-4 accent-[#2fa9bf] cursor-pointer"
        />
      </td>
      <td className={adminTableCell}>{record.case_id}</td>
      <td className={adminTableCell}>{record.case_owner}</td>
      <td className={adminTableCell}>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset capitalize ${statusStyles(
            record.status
          )}`}
        >
          {record.status || "—"}
        </span>
      </td>
      <td className={adminTableCell}>{record.service_type}</td>
    </tr>
  ));

  if (error) console.log("caseok cases fetching error : ", error);

  const colCount = 5;

  return (
    <>
      <Helmet>
        <title>Case Ok</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Case OK"
          subtitle="Send completion emails for approved cases"
        />

        <AdminMetaRow
          left={
            <span>
              {caseOkCases.length} case{caseOkCases.length === 1 ? "" : "s"}{" "}
              on this page
            </span>
          }
          right={
            <>
              <AdminPrimaryButton
                onClick={handleSendEmails}
                disabled={emailState.loading}
              >
                {emailState.loading
                  ? "Sending…"
                  : `Send Email to Selected${
                      selectedCaseIds.size ? ` (${selectedCaseIds.size})` : ""
                    }`}
              </AdminPrimaryButton>
              <AdminPagination
                page={currentPage}
                totalPages={caseOkTotalPages}
                onPrev={() => goToPage(currentPage - 1)}
                onNext={() => goToPage(currentPage + 1)}
                disabled={isLoading}
              />
            </>
          }
        />

        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={`${adminTableHead} text-center`}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-[#2fa9bf] cursor-pointer"
                  />
                </th>
                <th className={adminTableHead}>Case ID</th>
                <th className={adminTableHead}>Patient/Job</th>
                <th className={adminTableHead}>Status</th>
                <th className={adminTableHead}>Service Type</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading cases…"
                />
              ) : error ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="error"
                  message="Couldn’t load cases. Please try again."
                />
              ) : caseOkCases.length === 0 ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message="No case OK cases found."
                />
              ) : (
                caseOkCases.map((record) => (
                  <TableRow key={record.case_id} record={record} />
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>

        {/* Confirm Modal */}
        {showConfirmModal ? (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border border-[#E8E8E4]">
              <div>
                <h3 className="text-base font-semibold text-[#4A4A48]">
                  Confirm email send
                </h3>
                <p className="text-sm text-[#8A8A85] mt-1.5 leading-relaxed">
                  Send completion emails for{" "}
                  <span className="font-medium text-[#2fa9bf]">
                    {selectedCaseIds.size}
                  </span>{" "}
                  selected case{selectedCaseIds.size === 1 ? "" : "s"}?
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
                >
                  Cancel
                </button>
                <AdminPrimaryButton onClick={confirmSend}>
                  Yes, Send
                </AdminPrimaryButton>
              </div>
            </div>
          </div>
        ) : null}
      </AdminPageShell>
    </>
  );
};

export default CaseOk;
